import styles from "./MSMonthCalendar.module.css";
import { Button, Calendar, Col, Row, Select, Space, Spin, Tooltip } from "antd";
import {MasterScheduleEvent, MONTHS, ProductionLine, ScheduledTruck, ShortTimeFormat} from "../../api/models";
import dayjs, { Dayjs } from "dayjs";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import MasterScheduleEventButtonModal from "./MasterScheduleEventButtonModal";
import {useMasterScheduleSlotsContext} from "../../contexts/MasterScheduleSlotsContext";
import Utils from "../../util/util";

const years = Array.from({ length: 20 }, (_, i) => dayjs().year() - 10 + i);

interface ProductionLineDetail {
    line?: string | undefined,
    bevLineOpen: number;
    iceLineOpen: number;
    iceSerials: ScheduledTruck[];
    bevSerials: ScheduledTruck[];
    firstTaktEvent: MasterScheduleEvent | undefined;
    iceTaktRate: number;
    bevTaktRate: number;
}

const MSMonthCalendar = (props: {
    onChangeEvent: (e:MasterScheduleEvent | undefined) => void;
    onDateChange: (date: Dayjs) => void;
    onSelectDay: (date: Dayjs) => void;
    isReadOnly: boolean;
} ) => {

    const msContext = useMasterScheduleSlotsContext();
    const { selectedProductionLines, lineWithEventsAsync, getProductionLineDetail} = msContext;

    const selectedDate = msContext.selectedDate || dayjs();

    const cellRender = (current: Dayjs, _info: any) => {

        const[firstWeekStart, lastWeekEnd] = Utils.getMonthStartAndEnd(selectedDate);

        if (current.isBefore(firstWeekStart) || current.isAfter(lastWeekEnd)) {
            return <div style={{ display: "none" }} />; // Skip rendering for extra days
        }

        const allLines = Object.values(ProductionLine)
            .map(l => getProductionLineDetail?.(current, l) )
            .reduce((acc, d) => {

                if ( !acc ) return acc;
                if ( !d ) return acc;

                acc.bevLineOpen += d.bevLineOpen;
                acc.iceLineOpen += d.iceLineOpen;
                acc.iceSerials.push(...d.iceSerials);
                acc.bevSerials.push(...d.bevSerials);
                acc.firstTaktEvent = acc.firstTaktEvent || d.firstTaktEvent;
                acc.iceTaktRate += d.firstTaktEvent?.iceTaktRate || 0;
                acc.bevTaktRate += d.firstTaktEvent?.bevTaktRate || 0;
                return acc;
            }, {
                bevLineOpen: 0,
                iceLineOpen: 0,
                iceSerials: new Array<ScheduledTruck>(),
                bevSerials: new Array<ScheduledTruck>(),
                iceTaktRate: 0,
                bevTaktRate: 0,
            } as ProductionLineDetail);

        const isHoliday = !(!!allLines.iceTaktRate || !!allLines.bevTaktRate)
        if (isHoliday )  {
            return <div className={styles["date-cell"]} key={`${current.format(ShortTimeFormat)}-cell`} style={{alignContent: "baseline"}}>
                <div className={styles["date-area"]}>{current.date()}</div>
                <div key={`${current.format("YYYY-MM-DD")}-event-name`}
                     className={styles["event-name"]} >
                    <MasterScheduleEventButtonModal
                        type="text"
                        style={{color: "inherit", padding: 0, fontSize: 20}}
                        disabled={props.isReadOnly}
                        startDate={current}
                        event={allLines.firstTaktEvent}
                        onChange={props.onChangeEvent}
                    >
                        {allLines.firstTaktEvent?.name || ""}
                    </MasterScheduleEventButtonModal>
                </div>
            </div>
        }

        var isAllLines = !selectedProductionLines?.length;
        const details = isAllLines
            ? [ allLines ]
            : selectedProductionLines
                ?.map(l => getProductionLineDetail?.(current, l) );

        return <div className={styles["date-cell"]} key={`${current.format(ShortTimeFormat)}-cell`} style={{alignContent: "baseline"}}>
            <div className={styles["date-area"]}>{current.date()}</div>
            {details?.map(d =>
                (<DayEvent
                    current={current}
                    detail={d}
                    isReadOnly={props.isReadOnly}
                    onChangeEvent={props.onChangeEvent}
                    onSelectDay={props.onSelectDay}
                />) )}
        </div>
    };

    const disabledDate = (date: Dayjs) => {
        // Disable dates not in the current month
        return !date.isSame(selectedDate, "month");
    };

    const headerRender = ({ value, onChange, onTypeChange }) => {
        const current = selectedDate;

        const prevMonth = () => {
            onChange(current.subtract(1, 'month'));
        };

        const nextMonth = () => {
            onChange(current.add(1, 'month'));
        };

        const onMonthChange = (month) => {
            const newValue = current.month(month);
            onChange(newValue);
        };

        const onYearChange = (year) => {
            const newValue = current.year(year);
            onChange(newValue);
        };

        return (

            <Row justify={"space-between"} style={{padding: "8px"}}>
                <style>
                    {`
              .date-switch .ant-select-selector {
                border-radius: 15px !important;
              }
            `}
                </style>
                <Col>{lineWithEventsAsync?.isLoading && <Spin/>}</Col>
                <Col className={styles["date-switch"]}>
                    <Button type="text" shape="circle" rootClassName="month-switch" onClick={prevMonth} icon={<LeftOutlined />} />

                    {/* Month */}
                    <Select
                        value={current.month()}
                        onChange={onMonthChange}
                        style={{ width: 120, margin: '0 8px' }}
                        options={MONTHS.map((month, index) => {return {value: index, key: index, label: month}})}
                    >
                    </Select>

                    {/* Year */}
                    <Select
                        value={current.year()}
                        onChange={onYearChange}
                        style={{ width: 120, marginRight: '8px' }}
                        options={years.map((year) => {return {value: year, key: year, label: year}})}
                    />

                    <Button type="text" shape="circle" rootClassName="month-switch" onClick={nextMonth} icon={<RightOutlined />} />
                </Col>
            </Row>

        );
    };

    return (
        <>
            <Calendar
                className={styles["month-calendar"]}
                fullCellRender={cellRender}
                onChange={props.onDateChange}
                mode="month"
                value={selectedDate}
                headerRender={headerRender}
                disabledDate={disabledDate}
            />
        </>
    );
};

const DayEvent = (props: {
    current: Dayjs,
    detail: ProductionLineDetail
    onSelectDay: (d: Dayjs) => void;
    onChangeEvent: (e: MasterScheduleEvent | undefined) => void;
    isReadOnly: boolean;
}) => {

    const { current, detail } = props;

    const key = current.format("YYYYMMDD");

    const iceColor = detail.iceLineOpen === 0 ? 'darkgreen' : 'rgba(255, 0, 0, 1)';
    const bevColor = detail.bevLineOpen === 0 ? 'darkgreen' : 'rgba(255, 0, 0, 1)';

    const isGood = (detail.iceLineOpen === 0 && detail.bevLineOpen === 0);
    const color = (isGood) ? 'darkgreen' : 'rgba(255, 0, 0, 1)';
    const background = (isGood) ? 'rgba(0, 255, 0, 0.25)' : 'rgba(255, 0, 0, 0.25)';
    const border = (isGood) ? '3px solid rgba(0, 255, 0, 0.7)' : '3px solid rgba(255, 0, 0, 0.7)';

    if (!detail.firstTaktEvent) {
        return <div key={`${key}-content}`}></div>;
    }

    const iceSlot = detail?.iceTaktRate || 0;
    const bevSlot = detail?.bevTaktRate || 0;

    const hasIce = !!iceSlot || !!detail.iceSerials.length;
    const hasBev = !!bevSlot || !!detail.bevSerials.length;

    const isHoliday = !(hasBev || hasIce)

    const isAllLines = !detail.line
    const title = isAllLines
        ? "All Lines"
        : [ detail.line ].map( l => l?.split('_').pop() )
            .map( ln => `Line ${ln}`)
            .join(', ');

    return (
        <div className={styles["single-line-cell"]}
             key={`${key}-cell`}
             style={{
                 background,
                 border,
                 color,
                 flex: isAllLines ? "1 1 0" : undefined,
             }}
        >
            <div style={{ fontSize: '15px', textAlign: "left", }} >
                {title}
            </div>

            <Button type={"text"} onClick={(e) => props.onSelectDay(current)}>
                <div style={{display: "flex", justifyContent: 'center', fontSize: "24px"}}>
                    <Space size={12}>
                        {isHoliday && <div style={{fontSize: "24px"}}>No Production</div>}
                        {hasIce && <Tooltip key={`${key}-ice-tooltip`}
                                            title={detail.iceSerials.map(t=>t.serialNumber)?.join(", ")}
                                            overlayInnerStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', color: 'white' }} >
                            <span style={{ color: iceColor }}>
                                  ICE: {!!detail.iceLineOpen ? detail.iceLineOpen : (!!iceSlot ? 'FULL' : 'N/A')}
                            </span>
                        </Tooltip>}
                        {hasBev && <Tooltip key={`${key}-bev-tooltip`}
                                            title={detail.bevSerials.map(t=>t.serialNumber)?.join(", ")}
                                            overlayInnerStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', color: 'white' }} >
                            <span style={{ color: bevColor }}>
                                  BEV: {!!detail.bevLineOpen ? detail.bevLineOpen : (!!bevSlot ? 'FULL' : 'N/A')}
                            </span>
                        </Tooltip>}
                    </Space>
                </div>
            </Button>

            <div style={{display: "flex", flexDirection: "row-reverse", fontSize: "13px"}}>
                <MasterScheduleEventButtonModal
                    type="text"
                    style={{color: "inherit", padding: 0}}
                    disabled={props.isReadOnly}
                    startDate={current}
                    event={detail.firstTaktEvent}
                    onChange={props.onChangeEvent}
                >
                    <Space>
                        <div>ICE: {iceSlot}</div>
                        <div>BEV: {bevSlot}</div>
                    </Space>
                </MasterScheduleEventButtonModal>
            </div>
        </div>
    );

}

export default MSMonthCalendar;


